import { useEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { OrderWrapper } from 'Modules/order/components/OrderWrapper/OrderWrapper'
import {
  AccessoriesStep,
  AddressStep,
  DeviceStep,
  SummaryStep,
} from 'Modules/order/components/Steps'
import { ProlongInfoStep } from 'Modules/order/components/Steps/ProlongInfoStep/ProlongInfoStep'
import { OrderFlowTypesEnum } from 'Modules/order/types/orderFlow.types'
import { showErrorModal } from 'Modules/order/utils/errorModal'
import { notEmpty } from 'Modules/order/utils/notEmpty.utils'

import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { Stepper, StepperStep } from 'Components/Stepper'

import { useStore } from 'Stores/index'

import { useActiveUser, useIsOrganisation, useModal } from 'Hooks'

import {
  mutationUpgradeDevice,
  mutationUpgradeDeviceVariables,
} from 'Constants/graphql/mutations/__generated__/mutationUpgradeDevice'
import { MUTATION_UPGRADE_DEVICE } from 'Constants/graphql/mutations/MutationUpgradeDevice'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

export const OrderProlong = observer(() => {
  const { orderStore, userStore, rentalStore, navigationStore } = useStore()

  const {
    setMutationError,
    setOrderedDevices,
    chosenAccessories,
    remarks,
    chosenShippingAddress,
    chosenShippingAddressLabel,
    chosenDevice,
    shouldSkipAccessories,
  } = orderStore
  const { user } = userStore
  const { fetchRentalDetails, rentalDetails } = rentalStore
  const { mainNavigationConfig } = navigationStore
  const history = useHistory()
  const isOrganisation = useIsOrganisation()

  const { activeOrganisation, activeEmployee } = useActiveUser()
  const { employeeId = '', rentalId } = useParams<{
    employeeId?: string
    rentalId: string
  }>()
  const activeEmployeeId = employeeId || activeEmployee?.id.toString()

  const { setModal, hideModal } = useModal()

  const [createUpgradeDeviceCaseItem, { loading: isMutationLoading }] =
    useMutation<mutationUpgradeDevice, mutationUpgradeDeviceVariables>(
      MUTATION_UPGRADE_DEVICE,
      {
        onCompleted: data => {
          if (data.upgradeDevice.caseItem.subCaseItems) {
            const filteredDevices =
              data.upgradeDevice.caseItem.subCaseItems?.filter(notEmpty)

            setOrderedDevices(filteredDevices)
          } else {
            setOrderedDevices(Number(data.upgradeDevice.caseItem.id))
          }

          setMutationError(false)

          const path = composePath({
            isOrganisation,
            params: {
              employeeId: activeEmployeeId,
              organisationId: activeOrganisation?.id,
            },
            paths: {
              employee: GLOBAL_ROUTE_PATHS.employee.orderConfirmation,
              organisation: activeEmployeeId
                ? GLOBAL_ROUTE_PATHS.organisation.employeeOrderConfirmation
                : GLOBAL_ROUTE_PATHS.organisation.orderConfirmation,
            },
          })

          history.replace(path)
        },
        onError: () => {
          setMutationError(true)

          showErrorModal(() => {
            hideModal()
            history.push(mainNavigationConfig.url)
          }, setModal)
        },
        variables: {
          input: {
            accessories: chosenAccessories.size
              ? [...chosenAccessories].map(([_id, accessory]) => ({
                  customerLabel: accessory?.customerLabel ?? '',
                  id: accessory.deviceDefinition.id,
                  quantity: accessory.quantity,
                }))
              : null,
            comment: remarks,
            device: {
              customerLabel: chosenDevice?.customerLabel ?? '',
              id: chosenDevice?.deviceDefinition.id ?? '',
              quantity: chosenDevice?.quantity ?? 1,
            },
            employeeID: activeEmployeeId,
            organisationID:
              activeEmployee?.organisation?.id.toString() ||
              activeOrganisation?.id.toString() ||
              '',
            previousRentalID: rentalDetails?.rental.id ?? '',
            previousStocklistItemID: rentalDetails?.rental.stocklistItem?.id,
            rentCategoryID: chosenDevice?.rentCategoryId || '',
            requestingUserID: user.userId?.toString() || '',
            shippingAddress: {
              _label: chosenShippingAddressLabel,
              city: chosenShippingAddress?.city || '',
              country: chosenShippingAddress?.country || '',
              name: chosenShippingAddress?.name || '',
              nameAddition: chosenShippingAddress?.nameAddition,
              street: chosenShippingAddress?.street || '',
              streetAddition: chosenShippingAddress?.streetAddition,
              zipcode: chosenShippingAddress?.zipcode || '',
            },
          },
        },
      },
    )

  useEffect(() => {
    if (rentalDetails?.rental.id.toString() === rentalId) {
      return
    }

    fetchRentalDetails({
      isOrganisationView: isOrganisation,
      rentalID: rentalId,
    })
  }, [rentalId, fetchRentalDetails, isOrganisation, rentalDetails?.rental.id])

  const orderProlongStepsConfig = useMemo(() => {
    const baseSteps: StepperStep[] = [
      {
        headingLabel: 'OrderProlong_extend_rental_header',
        renderStepContent: ({ handleNextStep }) => (
          <ProlongInfoStep handleNextStep={handleNextStep} />
        ),
      },
      {
        headingLabel: 'NewOrder_choose_device_header',
        renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
          <DeviceStep
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
            isProlongingPossible={
              rentalDetails?.rental.actions.showDeviceProlongingAction ?? false
            }
          />
        ),
        subHeadingLabel: 'NewOrder_choose_device_subheader',
      },
      {
        headingLabel: 'NewOrder_shipping_address_header',
        renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
          <AddressStep
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
          />
        ),
        subHeadingLabel: 'NewOrder_shipping_address_subheader',
      },
      {
        headingLabel: 'NewOrder_order_summary_header',
        renderStepContent: ({ handleSetCurrentStep, handlePreviousStep }) => (
          <SummaryStep
            activeEmployeeId={activeEmployeeId}
            handleOrderSubmit={createUpgradeDeviceCaseItem}
            handlePreviousStep={handlePreviousStep}
            isSubmitLoading={isMutationLoading}
            setCurrentOrderStep={handleSetCurrentStep}
          />
        ),
        subHeadingLabel: 'NewOrder_order_summary_subheader',
      },
    ]

    const accessoriesStep: StepperStep = {
      headingLabel: 'NewOrder_choose_accessories_header',
      renderStepContent: ({ handleNextStep, handlePreviousStep }) => (
        <AccessoriesStep
          handleNextStep={handleNextStep}
          handlePreviousStep={handlePreviousStep}
        />
      ),
      subHeadingLabel: 'NewOrder_choose_accessories_subheader',
    }

    if (shouldSkipAccessories) {
      return baseSteps
    }

    baseSteps.splice(1, 0, accessoriesStep)

    return baseSteps
  }, [
    activeEmployeeId,
    createUpgradeDeviceCaseItem,
    isMutationLoading,
    rentalDetails?.rental.actions.showDeviceProlongingAction,
    shouldSkipAccessories,
  ])

  if (!rentalDetails) {
    return <PageLoadingState />
  }

  return (
    <OrderWrapper
      employeeId={activeEmployeeId}
      orderFlowType={OrderFlowTypesEnum.PROLONG}
      renderOrderContent={() => <Stepper steps={orderProlongStepsConfig} />}
    />
  )
})
